import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Avatar,
  Stack,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Flag as FlagIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

// Dynamic document requirements - will be fetched from API

const ProcessDocsStep = ({ data, onChange, onFormSubmit }) => {
  const { user, tokenManager } = useAuth();

  const [userCountry, setUserCountry] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);



  // Get country directly
  const selectedCountry = userCountry || 'India';

  // Intern users only need to upload documents marked as internRequired
  const isIntern = user?.roles?.includes('intern');

  // Filter requirements based on user type
  const visibleRequirements = isIntern
    ? documentRequirements.filter(doc => doc.internRequired === true)
    : documentRequirements;

  // Fetch user country and set country automatically
  useEffect(() => {
    if (user?.id) {
      fetchUserCountry();
    }
  }, [user?.id]);

  // Fetch documents for the current user
  useEffect(() => {
    if (user?.id) {
      fetchDocuments();
    }
  }, [user?.id]);

  // Fetch document requirements when country changes
  useEffect(() => {
    if (userCountry) {
      fetchDocumentRequirements(userCountry);
    }
  }, [userCountry]);

  const fetchUserCountry = async () => {
    if (!user?.id) return;

    try {
      const response = await fetch(`${API_URL}/onboarding/user-country/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.country) {
          setUserCountry(result.country);
        }
      }
    } catch (error) {
      console.error('Error fetching user country:', error);
    }
  };

  const fetchDocuments = async () => {
    if (!user?.id) return;

    setLoadingDocuments(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('module', 'Process Document');
      queryParams.append('subModule', 'Employee Document');
      queryParams.append('userId', user.id);

      const response = await fetch(`${API_URL}/documents/user?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Fetched documents:', result.documents); // Debug log
        setDocuments(result.documents || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const fetchDocumentRequirements = async (country) => {
    setLoadingRequirements(true);
    try {
      const response = await fetch(`${API_URL}/document-requirements/country/${country}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setDocumentRequirements(result.requirements || []);
      } else {
        // Fallback to default requirements if API fails
        console.warn('Failed to fetch document requirements, using fallback');
        setDocumentRequirements(getDefaultRequirements(country));
      }
    } catch (error) {
      console.error('Error fetching document requirements:', error);
      // Fallback to default requirements
      setDocumentRequirements(getDefaultRequirements(country));
    } finally {
      setLoadingRequirements(false);
    }
  };

  // Fallback function for default requirements
  const getDefaultRequirements = (country) => {
    const defaultRequirements = {
      India: [
        { id: 'pan_card', label: 'PAN Card', required: true, description: 'Permanent Account Number Card' },
        { id: 'aadhar_card', label: 'Aadhar Card', required: true, description: 'Unique Identification Number' },
        { id: 'passport', label: 'Passport', required: true, description: 'Valid Passport' },
        { id: 'photo', label: 'Photo', required: true, description: 'Recent Passport Size Photo' },
      ],
      Vietnam: [
        { id: 'mst_card', label: 'MST Card', required: true, description: 'Tax Identification Number Card' },
        { id: 'citizen_id', label: 'Citizen ID Card', required: true, description: 'National Identity Card' },
        { id: 'passport', label: 'Passport', required: true, description: 'Valid Passport' },
        { id: 'photo', label: 'Photo', required: true, description: 'Recent Passport Size Photo' },
      ],
      Indonesia: [
        { id: 'npwp_card', label: 'NPWP Card/Certificate', required: true, description: 'Tax Identification Number' },
        { id: 'ktp_card', label: 'KTP/National ID Card', required: true, description: 'National Identity Card' },
        { id: 'passport', label: 'Passport', required: true, description: 'Valid Passport' },
        { id: 'photo', label: 'Photo', required: true, description: 'Recent Passport Size Photo' },
      ],
    };
    return defaultRequirements[country] || [];
  };





  const handleDownloadDocument = async (doc) => {
    try {
      const fileName = doc.filePath || doc.fileName;
      
      if (!fileName) {
        toast.error('File not found');
        return;
      }

      // Show downloading toast
      toast.info(`Downloading ${doc.documentName || fileName}...`);

      // Fetch the file with authorization header first
      const response = await fetch(`${API_URL}/documents/download/${fileName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Authentication failed. Please login again.');
        } else {
          toast.error('Download failed');
        }
        return;
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Determine file extension from original filename or content type
      let fileExtension = '';
      if (fileName.includes('.')) {
        fileExtension = fileName.split('.').pop().toLowerCase();
      } else {
        // Fallback based on content type
        const contentType = response.headers.get('content-type');
        if (contentType) {
          if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) {
            fileExtension = 'jpg';
          } else if (contentType.includes('image/png')) {
            fileExtension = 'png';
          } else if (contentType.includes('image/gif')) {
            fileExtension = 'gif';
          } else if (contentType.includes('image/webp')) {
            fileExtension = 'webp';
          } else if (contentType.includes('application/pdf')) {
            fileExtension = 'pdf';
          }
        }
      }
      
      // Get the filename from Content-Disposition header (backend formatted)
      let downloadName = doc.documentName || fileName;
      
      // Try to get the formatted filename from the response header
      const contentDisposition = response.headers.get('content-disposition');
      
      if (contentDisposition) {
        // Extract filename from Content-Disposition header
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          downloadName = filenameMatch[1];
        }
      } else {
        // Fallback: manually construct the filename if header is not available
        if (doc.documentName && doc.uploadedBy) {
          downloadName = `${doc.documentName} (${doc.uploadedBy})`;
        }
      }
      
      // Fallback: ensure proper filename with extension if header parsing fails
      if (fileExtension && !downloadName.toLowerCase().endsWith(`.${fileExtension}`)) {
        downloadName += `.${fileExtension}`;
      }
      
      // Create a download link with unique identifier
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      link.style.display = 'none';
      link.id = `download-link-${Date.now()}`; // Unique ID
      
      // Add to DOM and trigger download
      document.body.appendChild(link);
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        link.click();
        
        // Clean up after download starts
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          window.URL.revokeObjectURL(url);
        }, 500); // Increased delay for Chrome
      });

    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (doc) => {
    try {
      // Debug: Log the document object to see its structure
      console.log('Document object for deletion:', doc);
      console.log('Document ID fields:', {
        documentId: doc.documentId,
        id: doc.id,
        _id: doc._id
      });
      
      const response = await fetch(`${API_URL}/documents/${doc.documentId || doc.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        // Remove from local state - fix the filter logic
        const updatedDocuments = documents.filter((document) => 
          (document.documentId || document.id) !== (doc.documentId || doc.id)
        );
        setDocuments(updatedDocuments);
        toast.success('Document deleted successfully');
        
        // Trigger validation in parent component
        if (onFormSubmit) {
          onFormSubmit();
        }
      } else {
        const result = await response.json();
        toast.error(result.error || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file extension - allow PDF and image files
      const fileName = selectedFile.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'];
      
      if (!allowedExtensions.includes(fileExtension)) {
        toast.error('Only PDF and image files (JPG, JPEG, PNG, GIF, WEBP) are allowed. Please convert your document to one of these formats and try again.');
        setFile(null);
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      // Check if selected document type requires image only
      if (selectedDocumentType) {
        const documentConfig = visibleRequirements.find(doc => doc.id === selectedDocumentType);
        if (documentConfig && documentConfig.imageOnly && fileExtension === 'pdf') {
          toast.error(`${documentConfig.label} must be an image file. PDF is not allowed for this document type.`);
          setFile(null);
          e.target.value = '';
          return;
        }
      }
      
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (selectedFile.size > maxSize) {
        toast.error('File size too large. Maximum file size is 10MB.');
        setFile(null);
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedDocumentType) {
      toast.error('Please select both file and document type');
      return;
    }

    if (!userCountry) {
      toast.error('Please complete your onboarding form first to set your country');
      return;
    }

    // Check if document already exists
    const documentConfig = visibleRequirements.find(
      doc => doc.id === selectedDocumentType
    );
    
    const existingDoc = documents.find(doc => doc.documentName === documentConfig.label);
    if (existingDoc) {
      toast.error('This document type has already been uploaded');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('module', 'Process Document');
      uploadData.append('subModule', 'Employee Document');
      uploadData.append('documentName', documentConfig.label);
      uploadData.append('description', documentConfig.description);
      uploadData.append('uploadedBy', user.email);
      uploadData.append('userId', user.id);

      const response = await fetch(`${API_URL}/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
        body: uploadData,
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setUploadDialogOpen(false);
        setFile(null);
        setSelectedDocumentType('');
        
        // Add the new document to local state immediately
        const newDocument = result.document;
        setDocuments(prevDocs => [...prevDocs, newDocument]);
        
        // Trigger validation in parent component
        if (onFormSubmit) {
          onFormSubmit();
        }
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };







  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '20px', fontWeight: 600 }}>
          Document Processing
        </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '16px' }}>
            Complete this section by uploading the required documents. Please ensure all mandatory documents are uploaded to proceed with your onboarding process.
          </Typography>
      </Box>

      {/* Intern notice */}
      {isIntern && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>Intern Document Requirements</Typography>
          <Typography variant="body2">
            As an intern, you are only required to upload identity documents. Other documents like financial or experience records are not required for your onboarding.
          </Typography>
        </Alert>
      )}

      {/* Document Requirements */}
      <Alert severity="info" sx={{ mb: 3, '& .MuiAlert-icon': { alignSelf: 'flex-start', mt: 0.5 } }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '18px', fontWeight: 600 }}>
          Required Documents :
        </Typography>
        {loadingRequirements ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <>
            {documentRequirements.length > 0 && visibleRequirements.length < documentRequirements.length && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Showing {visibleRequirements.length} of {documentRequirements.length} documents required for interns only.
              </Alert>
            )}
            <Grid container spacing={2}>
            {visibleRequirements.map((docType) => (
            <Grid item xs={12} sm={6} key={docType.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={documents.some(doc => doc.documentName === docType.label)}
                  disabled
                  color="primary"
                />
                <Box>
                  <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '16px' }}>
                    {docType.label}
                    {docType.required && (
                      <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '14px' }}>
                    {docType.description}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
            </Grid>
          </>
        )}
      </Alert>

      {/* Upload Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => {
            if (userCountry) {
              setUploadDialogOpen(true);
            } else {
              toast.error('Please complete your onboarding form first to set your country');
            }
          }}
          disabled={!userCountry}
          sx={{ mb: 1, fontSize: '14px', fontWeight: 600 }}
        >
          Upload Document
        </Button>
        {!userCountry && (
          <Typography variant="caption" color="text.secondary">
            Complete your onboarding form first to upload documents
          </Typography>
        )}
      </Box>



      {/* Uploaded Documents */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '18px', fontWeight: 600 }}>
          Uploaded Documents
        </Typography>
                          {loadingDocuments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
          </Box>
          ) : documents.length === 0 ? (
            <Alert severity="info">
              No documents uploaded yet. Please upload the required documents for {selectedCountry}.
            </Alert>
          ) : (
                  <Grid container spacing={3}>
                {documents.map((doc) => (
          <Grid item xs={12} sm={6} md={4} key={doc.documentId || doc.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                    {doc.documentName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={`Uploaded: ${new Date(
                      doc.uploadedAt || doc.uploadDate
                    ).toLocaleDateString()}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </CardContent>
              <CardActions>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadDocument(doc)}
                  sx={{ fontSize: '14px', fontWeight: 600 }}
                >
                  Download
                </Button>
                <Button
                  startIcon={<DeleteIcon />}
                  color="error"
                  onClick={() => handleDeleteDocument(doc)}
                  sx={{ fontSize: '14px', fontWeight: 600 }}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
        )}
      </Box>

      {/* Upload Document Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          Upload Document
          <IconButton
            aria-label="close"
            onClick={() => setUploadDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Document Type</InputLabel>
                <Select
                value={selectedDocumentType}
                onChange={(e) => setSelectedDocumentType(e.target.value)}
                label="Document Type"
              >
                {visibleRequirements.map((docType) => (
                  <MenuItem key={docType.id} value={docType.id}>
                    {docType.label}
                    {docType.required && ' (Required)'}
                  </MenuItem>
                ))}
                </Select>
              </FormControl>

            <Box sx={{ mb: 2 }}>
              <input
                key={`doc-upload-${selectedDocumentType}-${
                  visibleRequirements.find(d => d.id === selectedDocumentType)?.imageOnly ?? 'default'
                }`}
                accept={
                  selectedDocumentType && 
                  visibleRequirements.find(doc => doc.id === selectedDocumentType)?.imageOnly
                    ? "image/*"
                    : ".pdf,.jpg,.jpeg,.png,.gif,.webp"
                }
                style={{ display: 'none' }}
                id="document-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="document-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                fullWidth
                >
                  Choose PDF or Image File
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {selectedDocumentType && 
                  visibleRequirements.find(doc => doc.id === selectedDocumentType)?.imageOnly
                    ? "Accepted formats: JPG, JPEG, PNG, GIF, WEBP (Images only). Maximum file size: 10MB"
                    : "Accepted formats: PDF, JPG, JPEG, PNG, GIF, WEBP. Maximum file size: 10MB"
                }
              </Typography>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </Box>
            </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !file || !selectedDocumentType}
          >
            {uploading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Uploading...
              </>
            ) : (
              'Upload Document'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcessDocsStep; 